import React, { useEffect, useState } from 'react';
import { useAppDispatch } from 'ducks/hooks';
import Box from 'ui/Box';
import {
  autofillDoorType,
  autofillFrameLayout,
  cornerWallLeftWidth,
  cornerWallRightWidth,
  setAutoFillDoorType,
  setCornerWallInfoSheetVisible,
  setSingleWallInfoSheetVisible
} from 'rangeConfig/pax/features/AutofillPax/AutofillPax';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { AutoFillDoorTypeEnum } from '@inter-ikea-storageone/range-pax/src/api/components/AutofillSingleWallComponent';
import styled from 'styled-components';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import Title from 'ui/Typography/Title';
import InformationCircle from '@ingka/ssr-icon/paths/information-circle';
import { useBehaviorSystem, useBehaviorTracker } from 'contexts/behaviors';
import { getSelectedGroupId } from 'ducks/products';
import { isMobile } from 'utils/platform';
import { getMajorityFrameDepth } from '../AutofillUtils';
import { useCore } from 'contexts/core';
import Button, { ButtonTypeEnum } from 'ui/Button/Button';
import SegmentedControl from '@ingka/segmented-control';

const StyledToggle = styled.div<{ padding: string }>`
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  line-height: 1.5;
  margin-bottom: 1rem;
  justify-content: space-between;
  padding: ${({ padding }) => padding};
`;

const TitleAndInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 1rem;
`;

const StyledTitle = styled(Title)`
  display: flex;
  margin-right: 0.5rem;
`;

const AutoFillOptimizeSpaceDoorTypeToggle = () => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const selectedDoorType = useSelector(autofillDoorType);
  const selectedLayout = useSelector(autofillFrameLayout);
  const cornerWallWallLeftWidth = useSelector(cornerWallLeftWidth);
  const cornerWallWallRightWidth = useSelector(cornerWallRightWidth);
  const [disabled, setDisabled] = useState(false);
  const behaviorSystem = useBehaviorSystem();
  const behaviorTracker = useBehaviorTracker();
  const selectedGroupId = useSelector(getSelectedGroupId);
  const coreApi = useCore();
  const frameDepth = getMajorityFrameDepth(coreApi);

  useEffect(() => {
    if (selectedDoorType === AutoFillDoorTypeEnum.NONE && selectedLayout === 1) {
      if (
        (cornerWallWallLeftWidth === 860 || cornerWallWallRightWidth === 860) &&
        frameDepth === 580
      ) {
        setDisabled(true);
        return;
      }
      if (
        (cornerWallWallLeftWidth === 1130 || cornerWallWallRightWidth === 1130) &&
        frameDepth === 350
      ) {
        setDisabled(true);
        return;
      }
    }
    setDisabled(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cornerWallWallLeftWidth, cornerWallWallRightWidth, selectedLayout]);

  const handleDoorTypeToggle = (type: AutoFillDoorTypeEnum) => {
    behaviorSystem.startReport(`autofill-door-type-toggle-${type}`, {
      groupId: selectedGroupId,
      menuType: 'sheet',
      widgetId: 'autofill-door-type-toggle',
      meta: { option: type }
    });

    dispatch(setAutoFillDoorType(type));
  };

  useEffect(() => {
    if (selectedLayout === 1 && selectedDoorType === AutoFillDoorTypeEnum.SLIDING) {
      dispatch(setAutoFillDoorType(AutoFillDoorTypeEnum.NONE));
    }
  }, [dispatch, selectedDoorType, selectedLayout]);

  const handleInfoButton = () => {
    behaviorTracker
      .withContext({
        groupId: selectedGroupId,
        menuType: 'sheet',
        widgetId: 'autofill-door-type-information-button',
        meta: { actionTaken: true }
      })
      .custom(() => {
        if (selectedLayout === 0) {
          dispatch(setSingleWallInfoSheetVisible(true));
        } else {
          dispatch(setCornerWallInfoSheetVisible(true));
        }
      });
  };

  return (
    <Box>
      <TitleAndInfoWrapper>
        <StyledTitle
          id="autofill-change-frame-layout-heading"
          type={ThemeFontStyleTypeEnum.headingXSmall}
        >
          {formatMessage({ id: 'storageone.autofillMenuDoorTypeTitle' })}
        </StyledTitle>
        <Button
          onClick={handleInfoButton}
          id="autofill-door-type-info-button"
          ssrIcon={InformationCircle}
          size="xsmall"
          iconOnly
          type={ButtonTypeEnum.tertiary}
        />
      </TitleAndInfoWrapper>
      <StyledToggle padding={isMobile() ? '0 1rem' : '0'}>
        <SegmentedControl
          items={[
            {
              label: formatMessage({ id: 'storageone.autofillMenuDoorTypeHinged' }),
              value: AutoFillDoorTypeEnum.HINGED,
              disabled: disabled
            },
            {
              label: formatMessage({ id: 'storageone.autofillMenuDoorTypeNoDoors' }),
              value: AutoFillDoorTypeEnum.NONE
            },
            {
              label: formatMessage({ id: 'storageone.autofillMenuDoorTypeSliding' }),
              value: AutoFillDoorTypeEnum.SLIDING,
              disabled: selectedLayout !== 0
            }
          ]}
          data-testid="pax-autofill-door-type-toggle"
          activeIndex={Object.keys(AutoFillDoorTypeEnum).indexOf(selectedDoorType)}
          onClick={(event: any) => handleDoorTypeToggle(event.target.value as AutoFillDoorTypeEnum)}
        />
      </StyledToggle>
    </Box>
  );
};

export default AutoFillOptimizeSpaceDoorTypeToggle;
